/**
 * /dashboard/security — Org-admin UI for organization-level security
 * controls that apply to every member, regardless of role.
 *
 * Current controls (Batch 2 PRs #1 + #2 + #3, enterprise compliance closure):
 *
 *   • requireMfaForPasswordAuth — when ON, every member who signs in
 *     with email + password must have TOTP enabled. Members who don't
 *     are blocked at /api/auth/login with a 403 +
 *     `mfaEnrollmentRequired: true` envelope and a short-lived
 *     enrolment token that unlocks /api/auth/mfa/setup so they can
 *     complete enrolment without a session. SSO-authed users are
 *     unaffected — the IdP is responsible for second-factor there.
 *
 *   • aiOptOutEnabled (mig 148) — when ON, every LLM-backed surface
 *     in Rynora refuses with HTTP 403 + code ORG_AI_DISABLED for any
 *     request made while scoped to this org. Covers /api/intent/*
 *     (parse, explain, rca, vision, tool-call, …),
 *     /api/scripts/generate, the worker-driven flake-detective
 *     narrative + release-readiness verdict + tag-suggest. SSO,
 *     deterministic checks, visual baselines, etc. are unaffected.
 *
 *   • Audit-log SIEM webhook endpoints (mig 149) — admins can
 *     register one or more HTTPS endpoints that receive every new
 *     audit_log row within ~30s. Each endpoint gets a per-endpoint
 *     HMAC-SHA256 secret returned exactly once at create time;
 *     rotation issues a new secret atomically and old signatures
 *     stop verifying on the next worker tick.
 *
 * Future PRs will add more controls to this page (AI output
 * provenance badge, DSAR self-service, etc.) — having a dedicated
 * /dashboard/security page means each subsequent toggle is a small
 * additive change.
 *
 * Wraps the existing API endpoints in routes/orgs.ts +
 * routes/audit-siem.ts:
 *
 *   • GET   /api/orgs/:id/settings
 *   • PATCH /api/orgs/:id/settings  { requireMfaForPasswordAuth, aiOptOutEnabled }
 *   • GET    /api/orgs/:id/audit-siem
 *   • POST   /api/orgs/:id/audit-siem        { url, actionFilter? }
 *   • PATCH  /api/orgs/:id/audit-siem/:eid   { url?, enabled?, actionFilter?, rotateSecret? }
 *   • DELETE /api/orgs/:id/audit-siem/:eid
 *
 * Only owners and admins can flip security toggles; viewers see a
 * read-only banner. Every toggle change writes an
 * ORG_SECURITY_SETTINGS_UPDATED (MFA), ORG_AI_OPT_OUT_CHANGED (AI),
 * or AUDIT_SIEM_ENDPOINT_{CREATED,UPDATED,DELETED} row to
 * audit_log so procurement reviewers can answer "who turned this
 * on, when?".
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BrainCircuit,
  Copy,
  KeyRound,
  Loader2,
  PlusCircle,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Webhook,
} from 'lucide-react';
import { BrandMark } from '@rynora/theme';
import { getApiBaseUrl } from '../../../lib/config';
import { OrgCmkCard } from '../components/OrgCmkCard';

const API_BASE: string = getApiBaseUrl();

interface OrgSecurityPageProps {
  onBack: () => void;
}

interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  role?: 'owner' | 'admin' | 'member' | 'viewer';
}

interface OrgSettings {
  engineerHourlyCost?: number;
  minutesSavedPerHeal?: number;
  weeklyMottoHealth?: boolean;
  reliabilityScorecardPublic?: boolean;
  openReportsInNewTab?: boolean;
  requireMfaForPasswordAuth?: boolean;
  aiOptOutEnabled?: boolean;
  updatedAt?: string;
}

interface SiemEndpoint {
  id: string;
  orgId: string;
  url: string;
  enabled: boolean;
  hasSecret: boolean;
  actionFilter: string[] | null;
  lastForwardedAt: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureReason: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

function readActiveOrgFromToken(): { orgId: string | null } {
  const token = localStorage.getItem('rynora_token');
  if (!token) return { orgId: null };
  const parts = token.split('.');
  if (parts.length < 2) return { orgId: null };
  try {
    const json = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { orgId: typeof json.activeOrgId === 'string' ? json.activeOrgId : null };
  } catch {
    return { orgId: null };
  }
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('rynora_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function OrgSecurityPage({ onBack }: OrgSecurityPageProps) {
  const { orgId } = useMemo(readActiveOrgFromToken, []);

  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const [siemEndpoints, setSiemEndpoints] = useState<SiemEndpoint[]>([]);
  const [siemLoading, setSiemLoading] = useState(false);
  const [siemBusy, setSiemBusy] = useState(false);
  // `revealedSecrets` maps endpointId → freshly-issued secret. Only
  // populated by create + rotate responses; cleared on dismiss.
  // Storing it client-side (not in localStorage) means a page
  // reload drops the value, matching how every other secret
  // surface in Rynora behaves.
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, string>>({});
  const [newSiemUrl, setNewSiemUrl] = useState('');
  const [newSiemFilter, setNewSiemFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch<{ orgs: OrgSummary[] }>('/api/orgs');
        if (cancelled) return;
        setOrgs(res.orgs);
        const initial = res.orgs.find((o) => o.id === orgId) || res.orgs[0] || null;
        setSelectedOrg(initial);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load organizations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    if (!selectedOrg) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const s = await authedFetch<OrgSettings>(`/api/orgs/${selectedOrg.id}/settings`);
        if (cancelled) return;
        setSettings(s);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load organization settings.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOrg]);

  // SIEM endpoints — lazy-load alongside settings so the page
  // shows everything in one render. List endpoint is admin-only;
  // members trip the 403 path which we surface as a soft state
  // ("nothing to show") rather than a red banner because the
  // surrounding read-only banner already covers the "you're not
  // an admin" message.
  useEffect(() => {
    if (!selectedOrg) {
      setSiemEndpoints([]);
      return;
    }
    let cancelled = false;
    setSiemLoading(true);
    setRevealedSecrets({});
    (async () => {
      try {
        const r = await authedFetch<{ items: SiemEndpoint[] }>(`/api/orgs/${selectedOrg.id}/audit-siem`);
        if (cancelled) return;
        setSiemEndpoints(r.items || []);
      } catch {
        if (cancelled) return;
        setSiemEndpoints([]);
      } finally {
        if (!cancelled) setSiemLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedOrg]);

  const flash = (msg: string) => {
    setSavedFlash(msg);
    setTimeout(() => setSavedFlash(null), 3000);
  };

  const handleToggleMfaEnforcement = async (next: boolean) => {
    if (!selectedOrg || !settings) return;
    // Confirm the destructive direction (turning it ON). We deliberately
    // do NOT confirm when turning it off, because that's the reversible
    // direction and double-prompting feels punitive.
    if (next) {
      const ok = window.confirm(
        `Require MFA for ${selectedOrg.name}?\n\n` +
          `Every member who signs in with email + password will be forced ` +
          `to set up an authenticator app on their next sign-in. SSO users ` +
          `are unaffected — your identity provider already handles MFA.\n\n` +
          `This change is logged to the org audit log.`,
      );
      if (!ok) return;
    }

    const previous = settings.requireMfaForPasswordAuth === true;
    setSettings({ ...settings, requireMfaForPasswordAuth: next });
    setError(null);
    setSaving(true);
    try {
      const res = await authedFetch<OrgSettings>(`/api/orgs/${selectedOrg.id}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ requireMfaForPasswordAuth: next }),
      });
      setSettings(res);
      flash(next ? 'MFA enforcement enabled.' : 'MFA enforcement disabled.');
    } catch (err) {
      setSettings({ ...settings, requireMfaForPasswordAuth: previous });
      setError(err instanceof Error ? err.message : 'Failed to update setting.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAiOptOut = async (next: boolean) => {
    if (!selectedOrg || !settings) return;
    // Confirm the destructive direction (turning AI OFF). Switching
    // AI back on doesn't need a prompt — re-enabling LLM features is
    // a reversible "I changed my mind" action; disabling them needs
    // the buyer to acknowledge what they're losing.
    if (next) {
      const ok = window.confirm(
        `Disable Rynora AI features for ${selectedOrg.name}?\n\n` +
          `Every LLM-backed surface (NL parser, smart-action explainer, RCA, ` +
          `vision, tool-call, script generator, flake-detective narrative, ` +
          `release-readiness verdict, tag suggester) will refuse with a ` +
          `clear "AI disabled by admin" notice for every member of this org.\n\n` +
          `Deterministic features (visual baselines, scheduled runs, ` +
          `self-healing locators, etc.) are unaffected.\n\n` +
          `This change is logged to the org audit log under ` +
          `ORG_AI_OPT_OUT_CHANGED.`,
      );
      if (!ok) return;
    }

    const previous = settings.aiOptOutEnabled === true;
    setSettings({ ...settings, aiOptOutEnabled: next });
    setError(null);
    setSaving(true);
    try {
      const res = await authedFetch<OrgSettings>(`/api/orgs/${selectedOrg.id}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ aiOptOutEnabled: next }),
      });
      setSettings(res);
      flash(next ? 'AI features disabled for this organization.' : 'AI features re-enabled.');
    } catch (err) {
      setSettings({ ...settings, aiOptOutEnabled: previous });
      setError(err instanceof Error ? err.message : 'Failed to update setting.');
    } finally {
      setSaving(false);
    }
  };

  const parseFilter = (raw: string): string[] | null => {
    const cleaned = raw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return cleaned.length === 0 ? null : cleaned;
  };

  const handleCreateSiem = async () => {
    if (!selectedOrg) return;
    const url = newSiemUrl.trim();
    if (!url) {
      setError('Endpoint URL is required.');
      return;
    }
    setError(null);
    setSiemBusy(true);
    try {
      const res = await authedFetch<{ endpoint: SiemEndpoint; secret: string }>(
        `/api/orgs/${selectedOrg.id}/audit-siem`,
        {
          method: 'POST',
          body: JSON.stringify({
            url,
            actionFilter: parseFilter(newSiemFilter) ?? undefined,
          }),
        },
      );
      setSiemEndpoints((current) => [...current, res.endpoint]);
      setRevealedSecrets((current) => ({ ...current, [res.endpoint.id]: res.secret }));
      setNewSiemUrl('');
      setNewSiemFilter('');
      flash('SIEM endpoint added. Copy the secret now — it will not be shown again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add SIEM endpoint.');
    } finally {
      setSiemBusy(false);
    }
  };

  const handleToggleSiemEnabled = async (ep: SiemEndpoint, next: boolean) => {
    if (!selectedOrg) return;
    setSiemBusy(true);
    setError(null);
    try {
      const res = await authedFetch<{ endpoint: SiemEndpoint }>(`/api/orgs/${selectedOrg.id}/audit-siem/${ep.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: next }),
      });
      setSiemEndpoints((current) => current.map((e) => (e.id === ep.id ? res.endpoint : e)));
      flash(next ? 'Endpoint enabled.' : 'Endpoint disabled.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update endpoint.');
    } finally {
      setSiemBusy(false);
    }
  };

  const handleRotateSiemSecret = async (ep: SiemEndpoint) => {
    if (!selectedOrg) return;
    const ok = window.confirm(
      `Rotate the HMAC secret for ${ep.url}?\n\n` +
        `The current secret stops working on the next worker tick (within ~30s). ` +
        `Update your SIEM ingester to verify against the new secret immediately.`,
    );
    if (!ok) return;
    setSiemBusy(true);
    setError(null);
    try {
      const res = await authedFetch<{ endpoint: SiemEndpoint; secret?: string }>(
        `/api/orgs/${selectedOrg.id}/audit-siem/${ep.id}`,
        { method: 'PATCH', body: JSON.stringify({ rotateSecret: true }) },
      );
      setSiemEndpoints((current) => current.map((e) => (e.id === ep.id ? res.endpoint : e)));
      if (res.secret) {
        setRevealedSecrets((current) => ({ ...current, [ep.id]: res.secret as string }));
      }
      flash('Secret rotated. Copy the new value now — it will not be shown again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate secret.');
    } finally {
      setSiemBusy(false);
    }
  };

  const handleDeleteSiem = async (ep: SiemEndpoint) => {
    if (!selectedOrg) return;
    const ok = window.confirm(
      `Delete this SIEM endpoint?\n\n${ep.url}\n\n` +
        `Audit rows already forwarded stay in your SIEM; new rows stop being delivered immediately. ` +
        `This change is logged to the org audit log under AUDIT_SIEM_ENDPOINT_DELETED.`,
    );
    if (!ok) return;
    setSiemBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('rynora_token');
      const res = await fetch(`${API_BASE}/api/orgs/${selectedOrg.id}/audit-siem/${ep.id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      setSiemEndpoints((current) => current.filter((e) => e.id !== ep.id));
      setRevealedSecrets((current) => {
        const { [ep.id]: _drop, ...rest } = current;
        return rest;
      });
      flash('Endpoint removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete endpoint.');
    } finally {
      setSiemBusy(false);
    }
  };

  const copyToClipboard = (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value);
      flash('Copied to clipboard.');
    }
  };

  const role = selectedOrg?.role;
  const canEdit = role === 'owner' || role === 'admin';

  if (loading) {
    return (
      <div className="sso-settings-page">
        <div className="sso-settings-loading">
          <Loader2 size={28} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sso-settings-page">
      <header className="sso-settings-header">
        <button className="sso-settings-back" onClick={onBack} type="button">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <BrandMark variant="compact" />
      </header>

      <main className="sso-settings-main">
        <div className="sso-settings-title">
          <ShieldCheck size={22} aria-hidden="true" />
          <div>
            <h1>Security</h1>
            <p>Organization-level controls that apply to every member.</p>
          </div>
        </div>

        {orgs.length > 1 && (
          <div className="sso-settings-org-picker">
            <label htmlFor="sec-org-select">Organization</label>
            <select
              id="sec-org-select"
              value={selectedOrg?.id || ''}
              onChange={(e) => setSelectedOrg(orgs.find((o) => o.id === e.target.value) || null)}
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <div className="sso-settings-error">{error}</div>}
        {savedFlash && <div className="sso-settings-flash">{savedFlash}</div>}

        {!canEdit && selectedOrg && (
          <div className="sso-settings-status">
            You are signed in as <strong>{role || 'a non-admin'}</strong> on <strong>{selectedOrg.name}</strong>. Only
            owners and admins can change security settings.
          </div>
        )}

        <section className="sso-settings-form" aria-labelledby="mfa-enforcement-heading">
          <div className="sso-settings-field">
            <label
              id="mfa-enforcement-heading"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 15 }}
            >
              <KeyRound size={16} aria-hidden="true" /> Require multi-factor authentication
            </label>
            <p className="sso-settings-hint">
              When on, every member must have an authenticator app (TOTP) enabled before they can sign in with email and
              password. Members without MFA are blocked at sign-in and walked through enrolment automatically.
            </p>
            <p className="sso-settings-hint">
              <strong>SSO is unaffected.</strong> If your identity provider already enforces MFA, those users keep
              signing in normally — this toggle only governs the email/password path.
            </p>
          </div>

          <label className="sso-settings-checkbox">
            <input
              type="checkbox"
              checked={settings?.requireMfaForPasswordAuth === true}
              disabled={!canEdit || saving}
              onChange={(e) => handleToggleMfaEnforcement(e.target.checked)}
            />
            <span>
              <strong>Require MFA for password sign-in</strong>
              <small>
                {settings?.requireMfaForPasswordAuth
                  ? 'Enforcement is ON. New members will set up MFA at first sign-in.'
                  : 'Enforcement is OFF. Members can sign in without a second factor.'}
              </small>
            </span>
          </label>

          {settings?.updatedAt && (
            <p className="sso-settings-hint" style={{ marginTop: 12 }}>
              Last updated {new Date(settings.updatedAt).toLocaleString()}. Every change is recorded in the organization
              audit log under <code>ORG_SECURITY_SETTINGS_UPDATED</code>.
            </p>
          )}
        </section>

        <section className="sso-settings-form" aria-labelledby="ai-optout-heading" style={{ marginTop: 24 }}>
          <div className="sso-settings-field">
            <label
              id="ai-optout-heading"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 15 }}
            >
              <BrainCircuit size={16} aria-hidden="true" /> Rynora AI features
            </label>
            <p className="sso-settings-hint">
              By default every Rynora plan ships with optional LLM-backed assists — the natural-language step parser,
              smart-action explainer, RCA, vision extractor, script generator, flake-detective narrative, release
              verdict summary, and tag suggester.
            </p>
            <p className="sso-settings-hint">
              <strong>When this is OFF (default)</strong>, the AI features work as documented. Sensitive request bodies
              are pre-redacted and never used for vendor model training (see the no-training SLA on{' '}
              <a href="/trust/ai">/trust/ai</a>).
            </p>
            <p className="sso-settings-hint">
              <strong>When this is ON</strong>, every LLM call made on behalf of this org is refused at the API boundary
              with a clear "AI disabled by admin" notice. The deterministic path — your flows still run, visual
              baselines still compute, self-healing locators still work, schedules still fire — is unaffected.
              Worker-driven LLM narratives (flake-detective, release verdict) silently fall back to their deterministic
              equivalents so dashboards keep rendering.
            </p>
          </div>

          <label className="sso-settings-checkbox">
            <input
              type="checkbox"
              checked={settings?.aiOptOutEnabled === true}
              disabled={!canEdit || saving}
              onChange={(e) => handleToggleAiOptOut(e.target.checked)}
            />
            <span>
              <strong>Disable Rynora AI features for this organisation</strong>
              <small>
                {settings?.aiOptOutEnabled
                  ? 'AI is OFF. Every LLM-backed endpoint refuses with ORG_AI_DISABLED for this org.'
                  : 'AI is available. Members can use the LLM-backed assists.'}
              </small>
            </span>
          </label>

          {settings?.updatedAt && (
            <p className="sso-settings-hint" style={{ marginTop: 12 }}>
              Audit-log row for this toggle: <code>ORG_AI_OPT_OUT_CHANGED</code>. For the strongest possible isolation
              (no LLM in your VPC at all), see the Sovereign AI tier on <a href="/security">/security</a>.
            </p>
          )}
        </section>

        <section className="sso-settings-form" aria-labelledby="siem-heading" style={{ marginTop: 24 }}>
          <div className="sso-settings-field">
            <label
              id="siem-heading"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 15 }}
            >
              <Webhook size={16} aria-hidden="true" /> Audit-log SIEM forwarding
            </label>
            <p className="sso-settings-hint">
              Forward every new audit-log row to your own SIEM (Splunk, Datadog, Panther, Sumo, custom HTTPS) within ~30
              seconds. Each endpoint gets a per-endpoint <strong>HMAC-SHA256 secret</strong> returned exactly once; you
              can rotate it any time and the previous value stops verifying on the next tick.
            </p>
            <p className="sso-settings-hint">
              Signature header: <code>X-Rynora-Audit-Signature: sha256=&lt;hex&gt;</code>. The payload schema is stable
              (versioned); see the operator runbook for the JSON shape.
            </p>
          </div>

          {canEdit && (
            <div className="siem-create" style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              <input
                type="url"
                placeholder="https://splunk.acme.com/services/collector"
                value={newSiemUrl}
                onChange={(e) => setNewSiemUrl(e.target.value)}
                disabled={siemBusy}
                aria-label="SIEM endpoint URL"
                style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
              />
              <input
                type="text"
                placeholder="Optional action filter, comma-separated (e.g. LOGIN_BLOCKED_MFA_REQUIRED, ORG_AI_OPT_OUT_CHANGED)"
                value={newSiemFilter}
                onChange={(e) => setNewSiemFilter(e.target.value)}
                disabled={siemBusy}
                aria-label="SIEM action filter"
                style={{ padding: 8, border: '1px solid var(--border, #d0d7e2)', borderRadius: 6 }}
              />
              <button
                type="button"
                className="sso-settings-primary"
                disabled={siemBusy || newSiemUrl.trim().length === 0}
                onClick={handleCreateSiem}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}
              >
                <PlusCircle size={16} aria-hidden="true" /> Add endpoint
              </button>
            </div>
          )}

          {siemLoading && (
            <p className="sso-settings-hint" style={{ marginTop: 12 }}>
              Loading endpoints…
            </p>
          )}

          {!siemLoading && siemEndpoints.length === 0 && (
            <p className="sso-settings-hint" style={{ marginTop: 12 }}>
              No SIEM endpoints configured. Audit rows are still searchable in{' '}
              <a href="/dashboard/audit">/dashboard/audit</a>; add an endpoint above to mirror them to your own system.
            </p>
          )}

          {siemEndpoints.map((ep) => (
            <div
              key={ep.id}
              className="siem-endpoint"
              style={{
                marginTop: 16,
                padding: 12,
                border: '1px solid var(--border, #d0d7e2)',
                borderRadius: 8,
                background: ep.enabled ? 'var(--surface, #fff)' : 'var(--surface-muted, #f5f5f7)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <code style={{ wordBreak: 'break-all' }}>{ep.url}</code>
                <span
                  style={{
                    fontSize: 12,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: ep.enabled ? 'var(--success-bg, #e6f7ec)' : 'var(--warn-bg, #fef3c7)',
                    color: ep.enabled ? 'var(--success-fg, #057a3f)' : 'var(--warn-fg, #92400e)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ep.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {ep.actionFilter && ep.actionFilter.length > 0 && (
                <p className="sso-settings-hint" style={{ marginTop: 8 }}>
                  Action filter: <code>{ep.actionFilter.join(', ')}</code>
                </p>
              )}
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  rowGap: 4,
                  columnGap: 12,
                  marginTop: 8,
                  fontSize: 13,
                }}
              >
                <dt>Last success</dt>
                <dd>{ep.lastSuccessAt ? new Date(ep.lastSuccessAt).toLocaleString() : '—'}</dd>
                <dt>Last failure</dt>
                <dd>
                  {ep.lastFailureAt ? new Date(ep.lastFailureAt).toLocaleString() : '—'}
                  {ep.lastFailureReason && <span> ({ep.lastFailureReason})</span>}
                </dd>
                <dt>Consecutive failures</dt>
                <dd>{ep.consecutiveFailures}</dd>
              </dl>

              {revealedSecrets[ep.id] && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 8,
                    border: '1px solid var(--warn-fg, #92400e)',
                    background: 'var(--warn-bg, #fef3c7)',
                    borderRadius: 6,
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 600 }}>Secret (shown once — store it now):</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <code style={{ wordBreak: 'break-all', flex: 1 }}>{revealedSecrets[ep.id]}</code>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(revealedSecrets[ep.id])}
                      aria-label="Copy secret to clipboard"
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: 4,
                      }}
                    >
                      <Copy size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}

              {canEdit && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button type="button" disabled={siemBusy} onClick={() => handleToggleSiemEnabled(ep, !ep.enabled)}>
                    {ep.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    disabled={siemBusy}
                    onClick={() => handleRotateSiemSecret(ep)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <RefreshCw size={14} aria-hidden="true" /> Rotate secret
                  </button>
                  <button
                    type="button"
                    disabled={siemBusy}
                    onClick={() => handleDeleteSiem(ep)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-fg, #b91c1c)' }}
                  >
                    <Trash2 size={14} aria-hidden="true" /> Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </section>

        {selectedOrg && (
          <OrgCmkCard
            orgId={selectedOrg.id}
            orgSlug={selectedOrg.slug}
            canEdit={role === 'owner'}
            authToken={localStorage.getItem('rynora_token') ?? ''}
          />
        )}
      </main>
    </div>
  );
}
